// The sonar sweep's pass, as the chart piece sees it (card 59, R548,
// ui-map-interface.md §17.3.59).
//
// The pass itself is one program a backend owns. This file is the
// DISPATCHER: it holds the two shapes a caller fills in, and it picks the
// backend's pass from `gfx.backend` on the first draw. A chart piece is a
// singleton in the registry and holds no engine, so this is the one place
// that knows which backend is drawing.

import type { Gfx } from './types';
import type { GL } from '../gl/context';
import type { Gpu } from '../gpu/context';
import { GlSweepPass } from '../gl/sweep-pass';
import { GpuSweepPass } from '../gpu/sweep-pass';
import type { CameraUniforms } from '../layer';

/** One running reveal, as the pass draws it. */
export interface Sweep {
  /** The reveal's centre and radius, in world px. */
  x: number;
  y: number;
  r: number;
  /** Where the turning radius stands now, in radians. */
  angle: number;
  /** The afterglow's colour, as three floats in 0 to 1. */
  rgb: readonly [number, number, number];
}

/** The ramp and the radius, which every sweep of one frame shares. */
export interface SweepStyle {
  /** The four stops of the conic ramp, as a fraction of the turn. */
  stopAt: readonly number[];
  /** The alpha at each of those four stops. */
  stopAlpha: readonly number[];
  /** The turning radius: its alpha and its width in world px. */
  lineAlpha: number;
  lineWidth: number;
}

export class SweepPass {
  /** How many quads the last draw submitted. The test reads it. */
  draws = 0;

  private gl: GlSweepPass | null = null;
  private gpu: GpuSweepPass | null = null;

  /**
   * Draws every running sweep.
   *
   * `fade` is the chart's reading crossfade: the pass draws outside the
   * batches that carry it as a uniform, so it takes the same figure and
   * multiplies its own alpha by it.
   */
  draw(
    gfx: Gfx, cam: CameraUniforms,
    sweeps: readonly Sweep[], style: SweepStyle, fade: number,
  ): number {
    // Each pass answers its own `draws`, so the count is read once.
    this.draws = gfx.backend === 'webgpu'
      ? (this.gpu ?? (this.gpu = new GpuSweepPass()))
        .draw(gfx as Gpu, cam, sweeps, style, fade)
      : (this.gl ?? (this.gl = new GlSweepPass()))
        .draw((gfx as GL).gl, cam, sweeps, style, fade);
    return this.draws;
  }

  dispose(): void {
    this.gl?.dispose();
    this.gl = null;
    this.gpu?.dispose();
    this.gpu = null;
    this.draws = 0;
  }
}
